import i18n from '../i18n'
import { hasPartialServiceAccessTier } from '../hooks/useHasPermission'
import { isObject } from 'lodash'
import moment from 'moment'
import numbro from 'numbro'
import languages from 'numbro/dist/languages.min'
import { isYoutubeMediaItem } from '../domain/adCount/googleAdCount'

Object.values(languages).forEach((l) => numbro.registerLanguage(l))

// call_to_action is polymorphic (see domain/campaignSetup/schemas.js's
// callToActionSchema): a plain string (shared across every platform, or
// legacy data predating per-platform CTAs) or a {fb, google}-keyed object.
// Resolves it down to the flat string a given platform actually uses,
// mirroring the server-side resolve_call_to_action_for_platform()
// (fd-client-api-google-ads' app/core/integration/translation/base.py) so
// every reader — ad preview, the legacy Summary wizard preview, etc. —
// stays consistent with publish.
export function resolveCallToAction(callToAction, platform) {
  if (callToAction && typeof callToAction === 'object') return callToAction[platform] ?? ''
  return callToAction ?? ''
}

export const detectDeviceType = () => {
  const userAgent = navigator.userAgent

  if (/Mobi|Android|Tablet|iPad/i.test(userAgent)) {
    return 'mobile'
  } else {
    return 'desktop'
  }
}

/**
 * Display treatment: whether an entry is presented as a product — naming,
 * icons, hidden date/city/venue. Kept separate from isProductEntry so
 * presentation rules can diverge from functional behavior if needed.
 */
export const treatAsProductEntry = (category) => isProductEntry(category)

/**
 * Whether an entry's category marks it as a product/service (prisma-format
 * entry) rather than an actual event. The category value is 'product'
 * (displayed as "Product/Service") or 'prisma', depending on the data source.
 * Pure category check — use this for functional behavior (form variants,
 * required fields, routing, upload types). Not to be confused with the
 * per-account PRISMA/LIVE tier.
 */
export const isProductEntry = (category) =>
  ['product', 'prisma'].includes(String(category ?? '').toLowerCase())

/**
 * Level-1 terminology switch: whether the account speaks the product language
 * (prisma translation namespace) instead of the Live event wording. True for
 * PRISMA / full-service accounts.
 */
export const hasProductTerminology = () => hasPartialServiceAccessTier()

export const formatEventSubheadlineLocation = ({ city, venue, hall }) => {
  const safeCity = city || ''

  const localizedCity = i18n.t([`Locations.${safeCity}`, safeCity])
  if (localizedCity) {
    return `${localizedCity}${venue ? `: ${venue}` : ''}${hall ? `, ${hall}` : ''}`
  } else if (venue) {
    return `${venue}${hall ? `, ${hall}` : ''}`
  } else {
    return hall
  }
}

export const currencyFormat = function (amount, currency) {
  if (!amount && amount !== 0) {
    return '-'
  }

  let formattedAmount = 0

  if (amount) {
    const numberAmount = Number(amount)
    formattedAmount = numberAmount.toFixed(0).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.')
  }

  return currency === '$'
    ? `${currency}${formattedAmount}`
    : `${formattedAmount}${currency ? ' ' + currency : ''}`
}

export const numberWithThousandsSeparators = (
  n,
  { maxFractionDigits = 2, currency = null, suffix = '' } = {},
) => {
  let result = ''
  if (!n && n !== 0) {
    return '-'
  }

  // Map locale to numbro language code
  const locale = (localStorage.getItem('locale') ?? 'en-US').toLowerCase()
  let numbroLang = 'en'
  if (locale.startsWith('de')) {
    numbroLang = 'de'
  } else if (locale.startsWith('en')) {
    numbroLang = 'en'
  }
  numbro.setLanguage(numbroLang)

  result =
    currency && currency !== ''
      ? numbro(n).format({
          postfix: ` ${currency}`,
          thousandSeparated: true,
          mantissa: maxFractionDigits,
          optionalMantissa: true,
        })
      : numbro(n).format({
          thousandSeparated: true,
          optionalMantissa: true,
          mantissa: maxFractionDigits,
        })

  return `${result}${suffix}`
}

export const numberWithThousandsSeparatorsWithCurrencyPrefix = (
  n,
  { maxFractionDigits = 2, currency = null, suffix = '' } = {},
) => {
  let result = ''
  if (!n && n !== 0) {
    return '-'
  }

  const locale = localStorage.getItem('locale') ?? 'en-US'
  numbro.setLanguage(locale)

  result =
    currency && currency !== ''
      ? numbro(n).format({
          prefix: `${currency} `,
          thousandSeparated: true,
          mantissa: maxFractionDigits,
          optionalMantissa: true,
        })
      : numbro(n).format({
          thousandSeparated: true,
          optionalMantissa: true,
          mantissa: maxFractionDigits,
        })

  return `${result}${suffix}`
}

export const gcd = function (a, b) {
  return b === 0 ? a : gcd(b, a % b)
}

export const changeLanguage = function (i18n, lang) {
  i18n.changeLanguage(lang)
}

export const returnErrorMessage = function (error = {}) {
  if (error.displayTextOnly) {
    return error.response?.data.message || error.message || 'common.errors.inServerResponse'
  }

  if (error.response?.data?.message) {
    return 'common.serverErrors.' + error.response.data.message
  } else {
    return 'common.errors.inServerResponse'
  }
}

export const divideIntoSmallerArrays = function (arr, sizeOfSmallerArray) {
  const newArr = []
  arr.forEach((item) => {
    if (!newArr.length || newArr[newArr.length - 1].length === sizeOfSmallerArray) {
      newArr.push([])
    }
    newArr[newArr.length - 1].push(item)
  })
  return newArr
}

export const checkIfPasswordIsSecureEnough = function (password) {
  const response = {
    status: true,
    message: '',
  }

  if (!password) {
    response.message = 'common.errors.password.empty'
  } else if (password.length <= 7) {
    response.message = 'common.errors.password.tooShort'
  } else if (!/[A-Z]/.test(password)) {
    response.message = 'common.errors.password.missingUpperCase'
  } else if (!/[a-z]/.test(password)) {
    response.message = 'common.errors.password.missingLowerCase'
  } else if (!/[0-9]/.test(password)) {
    response.message = 'common.errors.password.missingNumber'
  } else if (password.replace(/[A-Za-z0-9]/g, '').length === 0) {
    response.message = 'common.errors.password.missingSpecial'
  }

  if (response.message) {
    response.status = false
  }

  return response
}

export const lowerFirstLetter = function (string) {
  return string.charAt(0).toLowerCase() + string.slice(1)
}

export const upperFirstLetter = function (string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export const isCampaignCreativeComplete = function (creative) {
  return (
    !!creative &&
    !!creative.body &&
    !!creative.body[0] &&
    !!creative.destination_url &&
    !!creative.destination_url[0] &&
    !!creative.headline &&
    !!creative.headline[0] &&
    !!creative.media
  )
}

export const capitalizeString = (str) => {
  if (!str || typeof str !== 'string') {
    return ''
  }

  return str
    .replaceAll('-', ' ')
    .split(' ')
    .map(function (word) {
      return word[0].toUpperCase() + word.substr(1)
    })
    .join(' ')
}

export const getQueryParam = (name) => {
  var query = window.location.search.substring(1)
  const params = query.split('&')

  const param = params
    .find((x) => {
      const qp = x.split('=')
      return qp && qp.length && qp[0] === name
    })
    ?.split('=')

  return param ? param[1] : undefined
}

export const objectToParams = (obj) => {
  if (!isObject(obj) || Object.keys(obj).length === 0) {
    return ''
  }

  return (
    '?' +
    Object.keys(obj)
      .filter((key) => obj[key] !== undefined)
      .map((key) => `${key}=${encodeURIComponent(obj[key])}`)
      .join('&')
  )
}

export const paramsToObject = (params) =>
  params
    ?.replace(/^\?/, '')
    .split('&')
    .reduce((acc, chunk) => {
      if (!chunk) {
        return acc
      }

      const [key, value] = chunk.split('=')
      return { ...acc, [key]: decodeURIComponent(value) }
    }, {}) || {}

export function joinPaths(...paths) {
  return paths
    .map((path) => path.trim().replace(/^\/|\/$/g, ''))
    .filter((path) => path.length > 0)
    .join('/')
}

export const flatten = (arr) => (arr ? arr.flat() : [])

export function snakeCaseToPascalCase(text) {
  return text
    .toLowerCase()
    .replace(/^[_]*(.)/, (_, c) => c.toUpperCase())
    .replace(/[_]+(.)/g, (_, c) => ' ' + c.toUpperCase())
}

export const getEnumMemberName = (enumValue) => {
  if (!enumValue) {
    return ''
  }

  if (enumValue.indexOf('.') > -1) {
    return enumValue.split('.')[1]
  }

  return enumValue
}

export const getMediaDimensions = async (mediaFile) => {
  if (!mediaFile) {
    return Promise.reject('No media file provided')
  }

  if (mediaFile.height && mediaFile.width) {
    return Promise.resolve({ width: mediaFile.width, height: mediaFile.height })
  }

  const getVideoDimensions = async (mediaFile) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video')
      video.preload = 'metadata'

      video.onloadedmetadata = () => {
        resolve({ width: video.videoWidth, height: video.videoHeight, duration: video.duration })
        URL.revokeObjectURL(video.src)
      }
      video.onerror = reject

      video.src = mediaFile instanceof File ? URL.createObjectURL(mediaFile) : mediaFile.url
    })
  }

  const getImageDimensions = async (mediaFile) => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        resolve({ width: img.width, height: img.height })
      }
      img.onerror = reject

      if (mediaFile instanceof File) {
        const reader = new FileReader()
        reader.onload = (e) => {
          img.src = e.target.result
        }
        reader.onerror = reject

        reader.readAsDataURL(mediaFile)
      } else {
        img.src = mediaFile.url
      }
    })
  }

  return mediaFile.type.includes('video')
    ? await getVideoDimensions(mediaFile)
    : await getImageDimensions(mediaFile)
}

export const shuffleArray = (array = []) => {
  const newArray = array.slice()
  for (let i = newArray.length - 1; i > 0; i--) {
    let j = Math.floor(Math.random() * (i + 1))
    let temp = newArray[i]
    newArray[i] = newArray[j]
    newArray[j] = temp
  }

  return newArray
}

export const generateAdPreviews = (regular = [], reels = []) => {
  const reelAdPreviews = reels.map((ad) => {
    return {
      data: ad,
      type: 'reel',
      id: ad.id,
      placement: 'Instagram reels',
      label: `${ad.tc}_${ad.variationId}`,
    }
  })
  const regularAdPreviews = regular.map((ad) => {
    const isTinyMedia = ad.media.width < 320 && ad.media.height < 200
    // 1:1 is eligible for right column
    const eligibleForRightColumn = ad.media.ratio === 1
    const regularAds = [
      {
        data: ad,
        type: 'facebook_web',
        id: ad.id,
        placement: 'Facebook feed desktop',
        label: `${ad.tc}_${ad.variationId}`,
        isGrouped: ad.isGrouped || false,
      },
      {
        data: ad,
        type: 'facebook_mobile',
        id: ad.id,
        placement: 'Facebook feed mobile',
        label: `${ad.tc}_${ad.variationId}`,
        isGrouped: ad.isGrouped || false,
      },
    ]

    if (!isTinyMedia) {
      regularAds.push({
        data: ad,
        type: 'instagram',
        id: ad.id,
        placement: 'Instagram feed mobile',
        label: `${ad.tc}_${ad.variationId}`,
        isGrouped: ad.isGrouped || false,
      })
    }

    if (eligibleForRightColumn) {
      regularAds.push({
        data: ad,
        type: 'facebook_right_column',
        id: ad.id,
        placement: 'Facebook right column',
        label: `${ad.tc}_${ad.variationId}`,
        isGrouped: ad.isGrouped || false,
      })
    }

    // If this is a grouped variation (feed + reel), add the reel preview
    if (ad.isGrouped && ad.reelMedia) {
      const reelAd = {
        tc: ad.tc,
        id: `${ad.id}-reel`,
        variationId: ad.variationId, // Same variationId as feed
        destination_url: ad.destination_url,
        media: ad.reelMedia,
        carousel_cards: ad.carousel_cards || [],
        is_reel: true,
        call_to_action: ad.call_to_action,
        body: ad.body,
        page_name: ad.page_name,
        profile_picture_url: ad.profile_picture_url,
        fb_info_loading: ad.fb_info_loading,
        fb_info_error: ad.fb_info_error,
      }

      regularAds.push({
        data: reelAd,
        type: 'reel',
        id: reelAd.id,
        placement: 'Instagram reels',
        label: `${ad.tc}_${ad.variationId}`,
        isGrouped: true,
      })
    }

    return regularAds
  })

  return [regularAdPreviews.flat(), reelAdPreviews]
}

export const getAdSuggestions = (creatives, fbPageInfo = [], t) => {
  let regular = [],
    reels = []

  creatives.map((creative, index) => {
    // getAdSuggestions only ever builds Meta placements (Facebook/Instagram/
    // Reels previews) — resolve to the 'fb' value before creative.call_to_action
    // (which may be the {fb, google} object, PD-3388-04-26) reaches translation.
    const rawCallToAction = resolveCallToAction(creative.call_to_action, 'fb')
    const call_to_action = t([
      `Recommendations.campaign.setup.call_to_action.${rawCallToAction}`,
      snakeCaseToPascalCase(rawCallToAction),
    ])

    const bodies = creative?.body?.filter(Boolean) ?? []
    const headlines = creative?.headline?.filter(Boolean) ?? []
    const { page_name, profile_picture_url } = fbPageInfo[index]?.content ?? {}
    const fb_info_loading = (fbPageInfo[index]?.loading || fbPageInfo[index] === undefined) ?? false
    const fb_info_error = fbPageInfo[index]?.error ?? false
    const tc = creative.tc

    // Combine all media from both arrays for processing
    // Mark each media with its source and index for proper variationId calculation
    const allMedia = [
      ...(creative.media || []).map((m, idx) => ({ ...m, source: 'media', mediaIndex: idx })),
      ...(creative.placement_media || []).map((m, idx) => ({
        ...m,
        source: 'placement_media',
        placementMediaIndex: idx,
      })),
    ]

    // Create maps for grouped media: placement_group_id -> { feed, reel }
    const groupMap = new Map()
    allMedia.forEach((mediaObject) => {
      const placementGroupId = mediaObject.placement_group_id
      if (placementGroupId) {
        if (!groupMap.has(placementGroupId)) {
          groupMap.set(placementGroupId, { feed: null, reel: null })
        }
        const group = groupMap.get(placementGroupId)
        if (mediaObject.ratio <= 1.4) {
          // Feed media
          if (!group.feed) {
            group.feed = mediaObject
          }
        } else {
          // Reel media
          if (!group.reel) {
            group.reel = mediaObject
          }
        }
      }
    })

    bodies.forEach((body, bodyIdx) => {
      ;(creative.destination_url || []).forEach((url, urlIdx) => {
        // Process media array first (primary feed media)
        ;(creative.media || []).forEach((mediaObject, mediaIdx) => {
          // Youtube vids are Google only
          if (isYoutubeMediaItem(mediaObject)) return
          if (mediaObject?.ratio <= 1.4) {
            // Feed media
            return headlines.forEach((headline, headlineIdx) => {
              const variationId = `${headlineIdx + 1}${bodyIdx + 1}${mediaIdx + 1}${urlIdx + 1}`

              // Check if this feed is part of a group
              const placementGroupId = mediaObject.placement_group_id
              const group = placementGroupId ? groupMap.get(placementGroupId) : null
              const matchingReel = group && group.reel ? group.reel : null

              if (matchingReel) {
                // This is a grouped variation: feed + reel
                regular.push({
                  tc: tc,
                  id: `${mediaIdx}-${tc}`,
                  variationId: variationId,
                  destination_url: url,
                  media: mediaObject,
                  call_to_action,
                  headline,
                  body,
                  page_name,
                  profile_picture_url,
                  fb_info_loading,
                  fb_info_error,
                  isGrouped: true,
                  reelMedia: matchingReel,
                })
              } else {
                // Regular feed-only variation
                regular.push({
                  tc: tc,
                  id: `${mediaIdx}-${tc}`,
                  variationId: variationId,
                  destination_url: url,
                  media: mediaObject,
                  call_to_action,
                  headline,
                  body,
                  page_name,
                  profile_picture_url,
                  fb_info_loading,
                  fb_info_error,
                })
              }
            })
          } else {
            // Reel in media array - check if it's part of a group
            const placementGroupId = mediaObject.placement_group_id
            const group = placementGroupId ? groupMap.get(placementGroupId) : null
            // Skip if there's a feed in the same group (it will be processed from placement_media)
            if (group && group.feed) {
              return // Skip this reel, it will be handled with the feed
            }
            // Standalone reel (not part of a group)
            reels.push({
              id: `${mediaIdx}-${tc}`,
              tc: tc,
              variationId: `x${bodyIdx + 1}${mediaIdx + 1}${urlIdx + 1}`,
              destination_url: url,
              media: mediaObject,
              is_reel: true,
              call_to_action,
              body,
              page_name,
              profile_picture_url,
              fb_info_loading,
              fb_info_error,
            })
          }
        })

        // Process placement_media array
        // Feed media in placement_media that are part of groups (where reel was added first)
        ;(creative.placement_media || []).forEach((placementMediaObject, placementMediaIdx) => {
          if (isYoutubeMediaItem(placementMediaObject)) return
          // Skip reels in placement_media that are part of groups (they're handled with feed)
          if (placementMediaObject.placement_group_id && placementMediaObject.ratio > 1.4) {
            const group = groupMap.get(placementMediaObject.placement_group_id)
            // Skip if there's a feed in the same group (it will be processed)
            if (group && group.feed) {
              return
            }
          }

          if (placementMediaObject?.ratio <= 1.4) {
            // Feed media in placement_media - this happens when reel was added first
            // Find the corresponding reel in media array to get the correct index for variationId
            const placementGroupId = placementMediaObject.placement_group_id
            const group = placementGroupId ? groupMap.get(placementGroupId) : null
            const matchingReel = group && group.reel ? group.reel : null

            if (matchingReel && matchingReel.source === 'media') {
              // Reel is in media array, use its index for variationId
              const reelMediaIdx = matchingReel.mediaIndex
              return headlines.forEach((headline, headlineIdx) => {
                const variationId = `${headlineIdx + 1}${bodyIdx + 1}${reelMediaIdx + 1}${urlIdx + 1}`

                // This is a grouped variation: feed (in placement_media) + reel (in media)
                regular.push({
                  tc: tc,
                  id: `pm-${placementMediaIdx}-${tc}`,
                  variationId: variationId,
                  destination_url: url,
                  media: placementMediaObject,
                  call_to_action,
                  headline,
                  body,
                  page_name,
                  profile_picture_url,
                  fb_info_loading,
                  fb_info_error,
                  isGrouped: true,
                  reelMedia: matchingReel,
                })
              })
            } else if (matchingReel) {
              // Reel is also in placement_media, use placement_media index
              return headlines.forEach((headline, headlineIdx) => {
                const variationId = `${headlineIdx + 1}${bodyIdx + 1}${matchingReel.placementMediaIndex + 1}${urlIdx + 1}`

                regular.push({
                  tc: tc,
                  id: `pm-${placementMediaIdx}-${tc}`,
                  variationId: variationId,
                  destination_url: url,
                  media: placementMediaObject,
                  call_to_action,
                  headline,
                  body,
                  page_name,
                  profile_picture_url,
                  fb_info_loading,
                  fb_info_error,
                  isGrouped: true,
                  reelMedia: matchingReel,
                })
              })
            }
            // If no matching reel, treat as standalone feed (shouldn't happen, but handle it)
          } else {
            // Standalone reel in placement_media (not part of a group)
            reels.push({
              id: `pm-${placementMediaIdx}-${tc}`,
              tc: tc,
              variationId: `x${bodyIdx + 1}${placementMediaIdx + 1}${urlIdx + 1}`,
              destination_url: url,
              media: placementMediaObject,
              is_reel: true,
              call_to_action,
              body,
              page_name,
              profile_picture_url,
              fb_info_loading,
              fb_info_error,
            })
          }
        })
      })
    })

    if (Array.isArray(creative.carousel_cards) && creative.carousel_cards.length) {
      const carouselMedia = creative.carousel_cards.find((card) => card.media?.url)?.media || null
      const carouselPlacementType =
        carouselMedia?.height / carouselMedia?.width > 1.4 ? 'reel' : 'feed'
      const carouselDestinationUrls = creative?.destination_url?.filter(Boolean) ?? []

      bodies.forEach((body, bodyIdx) => {
        carouselDestinationUrls.forEach((destinationUrl, urlIdx) => {
          const variationId = `1${bodyIdx + 1}1${urlIdx + 1}`
          const idBase = `carousel-${index}-${bodyIdx + 1}-${urlIdx + 1}-${tc}`
          const carouselCards = creative.carousel_cards.map((card) => ({
            ...card,
            call_to_action,
            destinationUrl,
          }))

          const carouselSuggestion = {
            tc,
            id: idBase,
            variationId,
            destination_url: destinationUrl,
            media: carouselMedia,
            call_to_action,
            headline: '',
            body,
            page_name,
            profile_picture_url,
            fb_info_loading,
            fb_info_error,
            carousel_cards: carouselCards,
          }

          if (carouselPlacementType === 'reel') {
            reels.push({
              ...carouselSuggestion,
              is_reel: true,
            })
          } else {
            regular.push(carouselSuggestion)
          }
        })
      })
    }
  })

  return [regular, reels]
}

export function isTouchDevice() {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0
}

export function hashObjectValues(obj) {
  function simpleHash(str) {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash |= 0 // convert to 32bit integer
    }
    return hash
  }

  // Convert object values to a string
  let valuesString = ''
  for (const key in obj) {
    // eslint-disable-next-line no-prototype-builtins
    if (obj.hasOwnProperty(key)) {
      valuesString += String(obj[key])
    }
  }

  // Return the hash of the concatenated string
  return simpleHash(valuesString)
}

export const strReplaceAt = (str, idx, val, totalChars = 1) => {
  return str.substr(0, idx) + val + str.substr(idx + totalChars)
}

export function uniqBy(array, property) {
  const seen = new Set()
  return array.filter((item) => {
    const value = item[property]
    if (!seen.has(value)) {
      seen.add(value)
      return true
    }
    return false
  })
}

export function localizeDateObj(date, defaultLocale = null, format = null) {
  const dateObj = new Date(date)
  if (format) {
    return moment(dateObj).format(format)
  }

  const locale = defaultLocale ?? i18n.language
  const options = { year: 'numeric', month: '2-digit', day: '2-digit' }
  const dateFormat = new Intl.DateTimeFormat(locale, options)

  return dateFormat.format(dateObj)
}

// Parse a 'YYYY-MM-DD' string into a Date in local time (no timezone shift)
export function parseDateYYYYMMDD(ymd) {
  if (!ymd || typeof ymd !== 'string') return null
  // split on T or an empty space to get the date
  const datePart = ymd.trim().split(/[T ]/)[0]

  const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return null
  const year = Number(match[1])
  const monthIdx = Number(match[2]) - 1
  const day = Number(match[3])
  return new Date(year, monthIdx, day)
}

// Format a Date as 'YYYY-MM-DD' using local date parts (no timezone shift)
export function formatDateYYYYMMDD(date) {
  if (!date) return null
  const d = new Date(date)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function isObjectEmpty(obj) {
  try {
    return Object.keys(obj).length === 0 && obj.constructor === Object
  } catch (e) {
    return true
  }
}

export function immutableSplice(arr, start, deleteCount, ...items) {
  return [...arr.slice(0, start), ...items, ...arr.slice(start + deleteCount)]
}

export function isErrorCode(error) {
  return typeof error === 'string' && error.startsWith('error.')
}

export function codifyAPIError({ error, status, message, data }) {
  console.error('API Failure', error, status, message, data)
  if (!error && message) {
    return message
  }

  // check if error is request canceled error by axios
  if (error?.isCanceled) {
    return 'common.errors.requestCanceled'
  }

  // Domain api modules pass the response body from unwrapEnvelope, not the AxiosError.
  const bodyCode = error?.isAxiosError ? undefined : error?.error_code
  const errorMsg = error?.isAxiosError
    ? (error.response?.data?.error_code ?? error.response?.message)
    : (bodyCode ?? error?.message)
  const codeKey = errorMsg ?? message
  if (!isErrorCode(codeKey)) {
    return 'common.errors.inServerResponse'
  }

  const payload = `common.serverErrors.${codeKey}`
  if (!i18n.exists(payload)) {
    // 400s are validation failures with backend-specific codes that don't all
    // have a translation yet — fall back to a generic "bad request" message
    // instead of showing the raw untranslated key.
    if (status === 400) {
      return 'common.serverErrors.generic_bad_request'
    }
    if (bodyCode) {
      return 'common.errors.inServerResponse'
    }
  }

  return payload
}

/**
 * Loads an image and resolves when it's safe to display.
 * Uses AbortSignal for early cancellation and cleans up listeners.
 *
 * Safari note: in rare cases `HTMLImageElement.decode()` can throw
 * synchronously. We wrap it in try/catch and treat decode failures
 * as non-fatal — once `onload` fires, we resolve regardless of decode()
 * outcome to keep UI responsive.
 */
export function loadImage(url, signal) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    let onAbort
    let settled = false

    const cleanup = () => {
      img.onload = null
      img.onerror = null
      if (onAbort && signal && typeof signal.removeEventListener === 'function') {
        signal.removeEventListener('abort', onAbort)
      }
    }

    const complete = (ok, err) => {
      if (settled) return
      settled = true
      cleanup()
      ok ? resolve() : reject(err || new Error('Image load error'))
    }

    img.decoding = 'async'

    onAbort = () => complete(false, new DOMException('Aborted', 'AbortError'))

    if (signal?.aborted) return onAbort()
    if (signal && typeof signal.addEventListener === 'function') {
      signal.addEventListener('abort', onAbort, { once: true })
    }

    img.onload = () => {
      const finalize = () => complete(true)
      try {
        img.decode ? img.decode().then(finalize).catch(finalize) : finalize()
      } catch {
        finalize()
      }
    }

    img.onerror = () => complete(false, new Error('Image load error'))

    img.src = url
  })
}

// localStorage wrapper
export const safeStorage = {
  get(key) {
    try {
      return localStorage.getItem(key)
    } catch (err) {
      console.error(err)
      return null
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, value)
      return true
    } catch (err) {
      console.error(err)
      return false
    }
  },
  remove(key) {
    try {
      localStorage.removeItem(key)
    } catch (err) {
      console.error(err)
    }
  },
  clear() {
    try {
      localStorage.clear()
    } catch (err) {
      console.error(err)
    }
  },
}
